/**
 * Final Review sections A–J (spec § 5.12) — 82 fields, transcribed from the
 * prototype's REVIEW table.
 *
 * The field KEYS are load-bearing, not cosmetic: rule L1 reads tc_price,
 * tc_qty and the seven tc_* extras; rule RF1 reads q_sampleReceived,
 * q_sampleApproved, c_status, sv_license, sv_type, w_length and ii_hs. Renaming
 * any of those silently breaks the landed cost or the risk flags, which are the
 * two numbers the buyer actually commits money against.
 *
 * Field shape: [key, label, type, options?]
 *   text | number | date | textarea | select | seg
 */

export const YN = ['', 'Yes', 'No'];

export const REVIEW_SECTIONS = [
  {
    id: 'tc',
    title: 'A. True cost',
    defaultOpen: true,
    fields: [
      ['tc_price', 'Final product price (per unit)', 'number'],
      ['tc_qty', 'Quantity', 'number'],
      ['tc_incoterm', 'Incoterm', 'select', ['', 'FOB', 'EXW', 'CIF', 'DDP', 'Other']],
      ['tc_freight', 'Ocean / air freight', 'number'],
      ['tc_broker', 'Customs broker', 'number'],
      ['tc_duty', 'Duty / tariff', 'number'],
      ['tc_inland', 'Inland transportation (USA)', 'number'],
      ['tc_insurance', 'Insurance', 'number'],
      ['tc_inspection', 'Inspection cost', 'number'],
      ['tc_other', 'Other costs', 'number'],
    ],
  },
  {
    id: 'ii',
    title: 'B. Import information',
    fields: [
      ['ii_hs', 'HS code', 'text'],
      ['ii_duty', 'Duty %', 'number'],
      ['ii_tariffs', 'Additional tariffs', 'text'],
      ['ii_origin', 'Country of origin', 'text'],
    ],
  },
  {
    id: 'q',
    title: 'C. Quality',
    uploads: 'quality_files',
    uploadTags: ['Sample Photo', 'Test Result', 'Inspection Report', 'Spec Sheet'],
    fields: [
      ['q_sampleReceived', 'Sample received?', 'select', YN],
      ['q_sampleApproved', 'Sample approved?', 'select', YN],
      ['q_sampleNotes', 'Sample notes', 'textarea'],
      ['q_specs', 'Product specifications confirmed?', 'select', YN],
      ['q_materials', 'Materials confirmed?', 'select', YN],
      ['q_defect', 'Defect tolerance', 'text'],
      ['q_inspection', 'Quality inspection allowed?', 'select', YN],
      ['q_thirdParty', 'Third-party inspection company', 'text'],
    ],
  },
  {
    id: 'c',
    title: 'D. Certifications',
    uploads: 'cert_files',
    uploadTags: ['Certificate'],
    fields: [
      ['c_type', 'Certification type', 'text'],
      ['c_number', 'Certificate number', 'text'],
      ['c_owner', 'Certificate owner', 'text'],
      ['c_model', 'Model covered', 'text'],
      ['c_exp', 'Expiration date', 'date'],
      ['c_status', 'Status', 'seg', ['Verified', 'Pending', 'Problem']],
    ],
  },
  {
    id: 'w',
    title: 'E. Warranty & after-sales',
    fields: [
      ['w_length', 'Warranty length', 'text'],
      ['w_covered', 'What is covered', 'textarea'],
      ['w_replacement', 'Replacement policy', 'text'],
      ['w_shipping', 'Who pays replacement shipping', 'text'],
      ['w_spare', 'Spare parts available?', 'select', YN],
      ['w_sparePrice', 'Spare parts price', 'text'],
      ['w_support', 'Technical support available?', 'select', YN],
    ],
  },
  {
    id: 'sv',
    title: 'F. Supplier verification',
    fields: [
      ['sv_type', 'Factory or trading company', 'select', ['', 'Factory', 'Trading Company', 'Not Sure']],
      ['sv_license', 'Business license', 'text'],
      ['sv_years', 'Years in business', 'number'],
      ['sv_address', 'Factory address', 'text'],
      ['sv_visit', 'Factory visit', 'select', ['', 'Done', 'Planned', 'No']],
      ['sv_export', 'Exporting to USA?', 'select', YN],
      ['sv_refs', 'US references', 'textarea'],
      ['sv_capacity', 'Production capacity / month', 'text'],
      ['sv_peak', 'Peak-season lead time', 'text'],
    ],
  },
  {
    id: 'ot',
    title: 'G. Order terms',
    fields: [
      ['ot_moq', 'MOQ', 'number'],
      ['ot_reorder', 'Reorder MOQ', 'number'],
      ['ot_deposit', 'Deposit %', 'number'],
      ['ot_balance', 'Balance %', 'number'],
      ['ot_method', 'Payment method', 'text'],
      ['ot_lead', 'Production lead time', 'text'],
      ['ot_validity', 'Price validity', 'text'],
      ['ot_tooling', 'Tooling fee', 'number'],
      ['ot_mold', 'Mold fee', 'number'],
      ['ot_setup', 'Setup fee', 'number'],
      ['ot_packaging', 'Packaging fee', 'number'],
    ],
  },
  {
    id: 'pl',
    title: 'H. Packaging & logistics',
    fields: [
      ['pl_carton', 'Carton dimensions', 'text'],
      ['pl_net', 'Net weight', 'text'],
      ['pl_gross', 'Gross weight', 'text'],
      ['pl_units', 'Units / carton', 'number'],
      ['pl_cpp', 'Cartons / pallet', 'number'],
      ['pl_psize', 'Pallet size', 'text'],
      ['pl_pweight', 'Pallet weight', 'text'],
      ['pl_20', 'Units per 20ft', 'number'],
      ['pl_40', 'Units per 40ft', 'number'],
      ['pl_40hq', 'Units per 40HQ', 'number'],
      ['pl_port', 'Shipping port', 'text'],
    ],
  },
  {
    id: 'pb',
    title: 'I. Private label / customization',
    fields: [
      ['pb_logo', 'Logo', 'text'],
      ['pb_pack', 'Custom packaging', 'text'],
      ['pb_color', 'Color', 'text'],
      ['pb_manual', 'Manual', 'text'],
      ['pb_upc', 'UPC', 'text'],
      ['pb_serial', 'Serial number', 'text'],
      ['pb_mod', 'Product modification', 'textarea'],
      ['pb_minqty', 'Minimum custom quantity', 'number'],
      ['pb_cost', 'Cost', 'text'],
    ],
  },
  {
    id: 'pr',
    title: 'J. Protection',
    fields: [
      ['pr_excl', 'Exclusivity available?', 'select', YN],
      ['pr_territory', 'Territory', 'text'],
      ['pr_product', 'Product exclusivity', 'text'],
      ['pr_custom', 'Custom-version exclusivity', 'text'],
      ['pr_mold', 'Mold ownership', 'text'],
      ['pr_design', 'Design ownership', 'text'],
      ['pr_ip', 'IP terms', 'textarea'],
    ],
  },
];

/** Every review key, for the CSV export's column list (spec § 8.3). */
export const REVIEW_KEYS = REVIEW_SECTIONS.flatMap((s) => s.fields.map(([k]) => k));

/** Label lookup, so the report sheets and CSV headers read like the form. */
export const REVIEW_LABELS = REVIEW_SECTIONS.reduce((acc, s) => {
  s.fields.forEach(([k, label]) => {
    acc[k] = label;
  });
  return acc;
}, {});
